import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Lock, VolumeX, Shield, User, LogOut, ChevronRight, Palette, Sun, Moon, Smartphone, Check, TrendingUp, Sparkles, Trophy, MessageCircle, Copy, Store, Users, Mail } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getExistingSubscription } from '../lib/push';

const NOTIF_GROUPS = [
  { key: 'plans', label: 'Plans', desc: 'Join requests, updates, reminders' },
  { key: 'social', label: 'Social', desc: 'Followers, likes, comments' },
  { key: 'messages', label: 'Messages', desc: 'New messages in group chats' },
  { key: 'discovery', label: 'Discovery', desc: 'Trending plans and people near you' },
  { key: 'safety', label: 'Safety', desc: 'Alerts from our safety team' },
];

const PRIVACY_OPTIONS = {
  privacy_messages: { label: 'Who can message you', options: ['everyone', 'followers', 'mutual', 'nobody'] },
  privacy_followers: { label: 'Who can see your followers', options: ['everyone', 'followers', 'nobody'] },
  privacy_following: { label: 'Who can see your following', options: ['everyone', 'only_me'] },
  privacy_plans: { label: 'Who can see your plans', options: ['everyone', 'followers'] },
};

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#B6E600]' : 'bg-[#E5E4DE]'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const [notifPrefs, setNotifPrefs] = useState({
    plans: true, social: true, messages: true, discovery: true, safety: true,
  });
  const [privacy, setPrivacy] = useState({
    privacy_followers: 'everyone',
    privacy_following: 'everyone',
    privacy_plans: 'everyone',
    privacy_messages: 'everyone',
    is_private: false,
  });
  const [mutedUsers, setMutedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({ global: true, categories: {} });
  const [emailCategories, setEmailCategories] = useState([]);
  const pushSupported = isPushSupported();

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/users/me/settings');
        if (data.notification_prefs) setNotifPrefs({ ...notifPrefs, ...data.notification_prefs });
        setPrivacy({
          privacy_followers: data.privacy_followers || 'everyone',
          privacy_following: data.privacy_following || 'everyone',
          privacy_plans: data.privacy_plans || 'everyone',
          privacy_messages: data.privacy_messages || 'everyone',
          is_private: !!data.is_private,
        });
        if (data.email_prefs) setEmailPrefs(data.email_prefs);
        if (data.email_categories) setEmailCategories(data.email_categories);
      } catch {}
      try {
        const { data } = await api.get('/users/me/muted');
        setMutedUsers(data);
      } catch {}
      if (pushSupported) {
        try {
          const sub = await getExistingSubscription();
          setPushEnabled(!!sub && Notification.permission === 'granted');
        } catch {}
      }
      setLoading(false);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailGlobal = async (val) => {
    const next = { ...emailPrefs, global: val };
    setEmailPrefs(next);
    setSaving(true);
    try { await api.put('/users/me/settings', { email_prefs: { global: val } }); } catch {}
    setSaving(false);
  };

  const handleEmailCategory = async (key, val) => {
    const next = { ...emailPrefs, categories: { ...emailPrefs.categories, [key]: val } };
    setEmailPrefs(next);
    setSaving(true);
    try {
      await api.put('/users/me/settings', { email_prefs: { categories: { [key]: val } } });
    } catch {}
    setSaving(false);
  };

  const handleNotifChange = async (key, val) => {
    const next = { ...notifPrefs, [key]: val };
    setNotifPrefs(next);
    setSaving(true);
    try {
      await api.put('/users/me/settings', { notification_prefs: next });
    } catch {}
    setSaving(false);
  };

  const handlePrivacyChange = async (key, val) => {
    const prev = privacy;
    const next = { ...privacy, [key]: val };
    setPrivacy(next);
    setSaving(true);
    try {
      await api.put('/users/me/settings', { [key]: val });
      updateUser({ ...user, [key]: val });
    } catch (e) {
      // Roll the toggle back if the save was rejected — UI must never lie
      // about server state (the testing agent caught this drift).
      setPrivacy(prev);
      window.alert(e?.response?.data?.detail || 'Could not update privacy setting. Please try again.');
    }
    setSaving(false);
  };

  const handleTogglePush = async (next) => {
    if (pushBusy) return;
    setPushBusy(true);
    if (next) {
      const r = await subscribeToPush();
      if (r.ok) setPushEnabled(true);
    } else {
      await unsubscribeFromPush();
      setPushEnabled(false);
    }
    setPushBusy(false);
  };

  const handleUnmute = async (userId) => {
    try {
      await api.delete(`/users/${userId}/mute`);
      setMutedUsers(prev => prev.filter(u => u.id !== userId));
    } catch {}
  };

  if (loading) {
    return (
      <div className="content-area px-4 pt-4 space-y-3">
        {[1,2,3,4].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="content-area animate-fade-in" data-testid="settings-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3 sticky top-0 bg-[#F7F6F2] z-10">
        <button onClick={() => navigate(-1)} data-testid="settings-back">
          <ArrowLeft size={22} className="text-[#111111]" />
        </button>
        <h1 className="text-xl font-bold text-[#111111]">Settings</h1>
        {saving && <span className="text-[10px] text-[#9B9EA3] ml-auto">Saving...</span>}
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Account */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Account</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            <button
              data-testid="settings-edit-profile"
              onClick={() => navigate('/edit-profile')}
              className="w-full flex items-center justify-between px-4 py-3.5 active:bg-[#F1F0EA] transition-colors"
            >
              <span className="text-sm text-[#111111]">Edit Profile</span>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
          </div>
        </section>

        {/* Business — Q25 */}
        <section data-testid="business-section">
          <div className="flex items-center gap-2 mb-3">
            <Store size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">For Businesses</h2>
          </div>
          <button
            data-testid="open-my-businesses"
            onClick={() => navigate('/business/mine')}
            className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
              <Store size={16} strokeWidth={2.4} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#111111]">My businesses</p>
              <p className="text-xs text-[#9B9EA3] mt-0.5">Register your venue · manage offers</p>
            </div>
            <ChevronRight size={18} className="text-[#9B9EA3]" />
          </button>
        </section>

        {/* Communities — Q28 */}
        <section data-testid="communities-section">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Communities</h2>
          </div>
          <button
            data-testid="open-communities"
            onClick={() => navigate('/communities')}
            className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
              <Users size={16} strokeWidth={2.4} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#111111]">My communities</p>
              <p className="text-xs text-[#9B9EA3] mt-0.5">Discover groups · keep plans alive</p>
            </div>
            <ChevronRight size={18} className="text-[#9B9EA3]" />
          </button>
        </section>

        {/* Admin — visible only to admin role */}
        {user?.role === 'admin' && (
          <section data-testid="admin-section">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={16} className="text-[#3F5200]" />
              <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Admin</h2>
            </div>
            <button
              data-testid="open-admin-reports"
              onClick={() => navigate('/admin/reports')}
              className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
                <Shield size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Trust &amp; Safety dashboard</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Review reports, take actions, view audit log</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-analytics"
              onClick={() => navigate('/admin/analytics')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#5856D6]/12 text-[#3D3BAD] flex items-center justify-center">
                <TrendingUp size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Business overview</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Growth, engagement, geography, top hosts</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-demo"
              onClick={() => navigate('/admin/demo')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#FFC107]/15 text-[#7A4F00] flex items-center justify-center">
                <Sparkles size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Demo seed lab</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Generate / wipe synthetic community for stress-testing</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-chats"
              onClick={() => navigate('/admin/chats')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#FF3B30]/12 text-[#FF3B30] flex items-center justify-center">
                <Shield size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Chat moderation</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Group chats · reported DMs · audit log</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-feedback"
              onClick={() => navigate('/admin/feedback')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#FF7A45]/15 text-[#FF7A45] flex items-center justify-center">
                <MessageCircle size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Beta feedback</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">NPS · responses · CSV export</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-venue-invites"
              onClick={() => navigate('/admin/venue-invites')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/20 text-[#3F5200] flex items-center justify-center">
                <Store size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Venue invites</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Track host invites · conversion funnel</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>

            <button
              data-testid="open-admin-waitlist"
              onClick={() => navigate('/admin/waitlist')}
              className="w-full mt-2 bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/20 text-[#3F5200] flex items-center justify-center">
                <Mail size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Waitlist</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">Marketing signups · filters · CSV export</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
          </section>
        )}

        {/* Reputation & Rewards */}
        <section data-testid="reputation-rewards-section">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Reputation &amp; Rewards</h2>
          </div>
          <button
            data-testid="open-how-points-work"
            onClick={() => navigate('/levels')}
            className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/20 text-[#3F5200] flex items-center justify-center">
              <Trophy size={16} strokeWidth={2.4} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#111111]">How points work</p>
              <p className="text-xs text-[#9B9EA3] mt-0.5">Levels · rewards · earning rules · FAQ</p>
            </div>
            <ChevronRight size={18} className="text-[#9B9EA3]" />
          </button>
        </section>

        {/* Beta — feedback survey */}
        <section data-testid="beta-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Beta</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            <button
              data-testid="open-beta-feedback"
              onClick={() => navigate('/feedback')}
              className="w-full p-4 flex items-center gap-3 active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#FF7A45]/15 text-[#FF7A45] flex items-center justify-center">
                <MessageCircle size={16} strokeWidth={2.4} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">Share beta feedback</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">2 min survey — helps us shape v1.0</p>
              </div>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
            <button
              data-testid="copy-feedback-link"
              onClick={async () => {
                const link = `${window.location.origin}/feedback`;
                try {
                  await navigator.clipboard.writeText(link);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 1800);
                } catch (e) {
                  window.prompt('Copy this link:', link);
                }
              }}
              className="w-full p-4 flex items-center gap-3 border-t border-black/[0.04] active:bg-[#F1F0EA] transition-colors"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#F1F0EA] text-[#6B6E73] flex items-center justify-center">
                {copiedLink ? <Check size={16} strokeWidth={3} /> : <Copy size={16} strokeWidth={2.4} />}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-[#111111]">{copiedLink ? 'Link copied!' : 'Copy shareable link'}</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5 truncate">{`${window.location.origin}/feedback`}</p>
              </div>
            </button>
          </div>
        </section>

        {/* Appearance — Light / Dark / System */}
        <section data-testid="appearance-section">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Appearance</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]">
            {[
              { key: 'system', label: 'Match system', desc: 'Follows your phone setting', Icon: Smartphone },
              { key: 'light',  label: 'Light',         desc: 'Bright, airy daytime',     Icon: Sun },
              { key: 'dark',   label: 'Dark',          desc: 'Cinematic luxury nightlife', Icon: Moon },
            ].map(({ key, label, desc, Icon }) => (
              <button
                key={key}
                data-testid={`theme-option-${key}`}
                onClick={() => setThemeMode(key)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F0EA] transition-colors"
              >
                <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${themeMode === key ? 'bg-[#B6E600]/20 text-[#3F5200]' : 'bg-[#F1F0EA] text-[#6B6E73]'}`}>
                  <Icon size={16} strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#111111]">{label}</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">{desc}</p>
                </div>
                {themeMode === key && (
                  <Check size={18} className="text-[#B6E600]" strokeWidth={3} />
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Email notifications — scalable, category-based */}
        <section data-testid="email-prefs-section">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Email notifications</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]">
            {/* Global kill switch */}
            <div className="flex items-center justify-between px-4 py-3.5" data-testid="email-global-row">
              <div className="pr-3 flex-1">
                <p className="text-sm text-[#111111] font-bold">Receive emails from ASAP</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">
                  When off, only critical security emails (password reset, email verification) are sent.
                </p>
              </div>
              <Toggle
                testId="email-global-toggle"
                checked={!!emailPrefs.global}
                onChange={handleEmailGlobal}
              />
            </div>
            {/* Per-category toggles — disabled when global is off */}
            {emailCategories.map(c => (
              <div key={c.key} className={`flex items-center justify-between px-4 py-3.5 ${!emailPrefs.global ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="pr-3 flex-1">
                  <p className="text-sm text-[#111111] font-medium">{c.label}</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">{c.description}</p>
                </div>
                <Toggle
                  testId={`email-cat-${c.key}-toggle`}
                  checked={emailPrefs.categories?.[c.key] !== undefined ? !!emailPrefs.categories[c.key] : !!c.default_on}
                  onChange={(v) => handleEmailCategory(c.key, v)}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Q33 — Smart Nudges entry */}
        <section className="-mt-2">
          <button
            data-testid="open-smart-nudges-btn"
            onClick={() => navigate('/settings/notifications')}
            className="w-full bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-3 active:bg-[#F1F0EA]"
          >
            <div className="w-10 h-10 rounded-xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} strokeWidth={2.4} />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-extrabold text-[#111]">Smart Nudges</p>
              <p className="text-[11px] text-[#6B6E73]">11 fine-grained categories · quiet hours · digests</p>
            </div>
            <ChevronRight size={18} className="text-[#9B9EA3]" />
          </button>
        </section>

        {/* Notifications */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">In-app & push notifications</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]">
            {pushSupported && (
              <div className="flex items-center justify-between px-4 py-3.5" data-testid="push-toggle-row">
                <div className="pr-3 flex-1">
                  <p className="text-sm text-[#111111] font-medium">Push notifications on this device</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">
                    {pushEnabled ? 'Enabled — you’ll receive alerts in the background.' : 'Off — turn on to get DMs, plan updates and safety alerts.'}
                  </p>
                </div>
                <Toggle
                  testId="push-device-toggle"
                  checked={pushEnabled}
                  onChange={handleTogglePush}
                />
              </div>
            )}
            {NOTIF_GROUPS.map(g => (
              <div key={g.key} className="flex items-center justify-between px-4 py-3.5">
                <div className="pr-3 flex-1">
                  <p className="text-sm text-[#111111] font-medium">{g.label}</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">{g.desc}</p>
                </div>
                <Toggle
                  testId={`notif-${g.key}-toggle`}
                  checked={!!notifPrefs[g.key]}
                  onChange={(v) => handleNotifChange(g.key, v)}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Lock size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Privacy</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]">
            {/* Private account master toggle */}
            <div className="px-4 py-3.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#111111]">Private account</p>
                <p className="text-[11px] text-[#9B9EA3] mt-0.5 leading-relaxed">
                  Only approved followers can see your stories, vibes, plans and connections. New follows become requests.
                </p>
              </div>
              <button
                role="switch"
                aria-checked={privacy.is_private}
                data-testid="privacy-private-toggle"
                onClick={() => handlePrivacyChange('is_private', !privacy.is_private)}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${privacy.is_private ? 'bg-[#111]' : 'bg-[#E5E4DE]'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${privacy.is_private ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {Object.entries(PRIVACY_OPTIONS).map(([key, cfg]) => (
              <div key={key} className="px-4 py-3.5">
                <p className="text-sm text-[#111111] font-medium mb-2">{cfg.label}</p>
                <div className="flex gap-1.5 flex-wrap">
                  {cfg.options.map(opt => (
                    <button
                      key={opt}
                      data-testid={`privacy-${key}-${opt}`}
                      onClick={() => handlePrivacyChange(key, opt)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border transition-all ${
                        privacy[key] === opt
                          ? 'bg-[#B6E600] text-[#111111] border-[#B6E600]'
                          : 'bg-[#F1F0EA] text-[#6B6E73] border-black/[0.06]'
                      }`}
                    >
                      {opt.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Muted Users */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <VolumeX size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Muted Users</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06]" data-testid="muted-users-list">
            {mutedUsers.length === 0 ? (
              <p className="text-sm text-[#9B9EA3] text-center py-6 px-4">You haven't muted anyone.</p>
            ) : (
              <div className="divide-y divide-black/[0.04]">
                {mutedUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`muted-user-${u.id}`}>
                    {u.profile_photo ? (
                      <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
                        {(u.display_name || '?').charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#111111] truncate">{u.display_name}</p>
                      <p className="text-xs text-[#9B9EA3] truncate">@{u.username}</p>
                    </div>
                    <button
                      data-testid={`unmute-btn-${u.id}`}
                      onClick={() => handleUnmute(u.id)}
                      className="text-xs font-medium text-[#3F5200] bg-[#B6E600]/15 px-3 py-1.5 rounded-full"
                    >
                      Unmute
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Safety Center */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Safety Center</h2>
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden divide-y divide-black/[0.04]">
            <button
              data-testid="settings-guidelines"
              onClick={() => navigate('/guidelines')}
              className="w-full flex items-center justify-between px-4 py-3.5 active:bg-[#F1F0EA] transition-colors"
            >
              <span className="text-sm text-[#111111]">Community Guidelines</span>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
            <button
              data-testid="settings-terms"
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between px-4 py-3.5 active:bg-[#F1F0EA] transition-colors"
            >
              <span className="text-sm text-[#111111]">Terms of Service</span>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
            <button
              data-testid="settings-privacy"
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between px-4 py-3.5 active:bg-[#F1F0EA] transition-colors"
            >
              <span className="text-sm text-[#111111]">Privacy Policy</span>
              <ChevronRight size={18} className="text-[#9B9EA3]" />
            </button>
          </div>
        </section>

        {/* Account — danger zone */}
        <section data-testid="account-danger-section">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-[#9B9EA3]" />
            <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Account</h2>
          </div>
          <button
            data-testid="open-delete-account"
            onClick={() => setShowDeleteAccount(true)}
            className="w-full bg-white rounded-2xl border border-[#FF3B30]/20 px-4 py-3.5 flex items-center justify-between active:bg-[#FF3B30]/5 transition-colors"
          >
            <span className="text-sm font-semibold text-[#FF3B30]">Delete account</span>
            <ChevronRight size={18} className="text-[#FF3B30]/60" />
          </button>
        </section>

        {/* Logout */}
        <button
          data-testid="settings-logout"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-white border border-black/[0.06] text-[#FF3B30] font-medium active:scale-[0.99] transition-transform"
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>

      {showDeleteAccount && (
        <DeleteAccountModal
          onClose={() => setShowDeleteAccount(false)}
          onSuccess={logout}
        />
      )}
    </div>
  );
}

function DeleteAccountModal({ onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = password.length > 0 && confirm.trim().toUpperCase() === 'DELETE';

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.delete('/auth/me', { data: { password, confirm } });
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete account.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-5" onClick={onClose}>
      <div
        className="w-full max-w-[400px] bg-white rounded-3xl p-6 animate-scale-in shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="delete-account-modal"
      >
        <h3 className="text-lg font-extrabold text-[#111111] mb-1">Delete your account?</h3>
        <p className="text-xs text-[#6B6E73] leading-relaxed mb-4">
          This permanently removes your profile, stories, vibes, follows, and joins.
          Plans you hosted will be cancelled. This cannot be undone.
        </p>
        {error && <p className="text-xs text-[#FF3B30] mb-3">{error}</p>}
        <div className="space-y-2.5">
          <input
            data-testid="delete-password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Confirm password"
            className="w-full bg-[#F1F0EA] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#FF3B30]/40 placeholder-[#B0B3B8]"
          />
          <input
            data-testid="delete-confirm"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder='Type "DELETE" to confirm'
            className="w-full bg-[#F1F0EA] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#FF3B30]/40 placeholder-[#B0B3B8]"
          />
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            data-testid="delete-cancel"
            className="flex-1 py-3 rounded-full bg-[#F1F0EA] text-[#111111] text-sm font-bold active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || submitting}
            data-testid="delete-submit"
            className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-sm font-bold active:scale-95 disabled:opacity-40"
          >
            {submitting ? 'Deleting…' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}
